import { useState } from 'react';
import { CollapsibleSection, ToggleRow, labelCls, SavedTip } from './shared';
import { useUiStore } from '../../../stores/uiStore';
import type { ThemeColor, FontSize, Density } from '../../../stores/uiStore';

export default function AppearancePanel() {
  const {
    themeColor, fontSize, density, sidebarDefaultCollapsed,
    setThemeColor, setFontSize, setDensity, setSidebarDefaultCollapsed,
  } = useUiStore();

  const [saved, setSaved] = useState(false);

  const colors: { key: ThemeColor; label: string; cls: string }[] = [
    { key: 'teal', label: '青碧', cls: 'bg-teal-500' },
    { key: 'slate', label: '铁灰', cls: 'bg-slate-500' },
    { key: 'emerald', label: '翠绿', cls: 'bg-emerald-500' },
    { key: 'cyan', label: '天青', cls: 'bg-cyan-500' },
    { key: 'rose', label: '纹红', cls: 'bg-rose-400' },
    { key: 'orange', label: '橙赤', cls: 'bg-orange-400' },
  ];

  const fontSizes: { key: FontSize; label: string }[] = [
    { key: 'sm', label: '小' },
    { key: 'md', label: '默认' },
    { key: 'lg', label: '大' },
  ];

  const densities: { key: Density; label: string }[] = [
    { key: 'compact', label: '紧凑' },
    { key: 'normal', label: '标准' },
    { key: 'loose', label: '宽松' },
  ];

  const handleColorChange = (color: ThemeColor) => {
    setThemeColor(color);
    showSaved();
  };

  const handleFontSizeChange = (size: FontSize) => {
    setFontSize(size);
    showSaved();
  };

  const handleDensityChange = (d: Density) => {
    setDensity(d);
    showSaved();
  };

  const handleSidebarToggle = () => {
    setSidebarDefaultCollapsed(!sidebarDefaultCollapsed);
    showSaved();
  };

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-3 md:space-y-6">
      {saved && <SavedTip />}

      <CollapsibleSection title="主题颜色">
        <div className="flex items-center gap-3 flex-wrap">
          {colors.map(c => (
            <button
              key={c.key}
              type="button"
              onClick={() => handleColorChange(c.key)}
              className="flex flex-col items-center gap-1.5 cursor-pointer group"
            >
              <span
                className={`w-8 h-8 rounded-full ${c.cls} transition-all ${
                  themeColor === c.key ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                }`}
              />
              <span className={`text-xs ${themeColor === c.key ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                {c.label}
              </span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3">选择主题色后立即生效，刷新页面后仍保持</p>
      </CollapsibleSection>

      <CollapsibleSection title="字号与密度" defaultOpen={false}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>字号大小</label>
            <div className="flex gap-2">
              {fontSizes.map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => handleFontSizeChange(f.key)}
                  className={`flex-1 py-2 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                    fontSize === f.key
                      ? 'border-teal-400 bg-teal-50 text-teal-600 font-medium'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>列表密度</label>
            <div className="flex gap-2">
              {densities.map(d => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => handleDensityChange(d.key)}
                  className={`flex-1 py-2 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                    density === d.key
                      ? 'border-teal-400 bg-teal-50 text-teal-600 font-medium'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="布局偏好" defaultOpen={false}>
        <ToggleRow
          icon="ri-layout-left-2-line"
          title="侧边栏默认折叠"
          desc="进入页面时侧边栏自动收起"
          checked={sidebarDefaultCollapsed}
          onChange={handleSidebarToggle}
        />
      </CollapsibleSection>
    </div>
  );
}
